import React, { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import { CalendarDays, Check, Loader2, ArrowLeft } from "lucide-react";
import { httpClient } from "../../HttpClient";

const JOURS = ["dimanche", "lundi", "mardi", "mercredi", "jeudi", "vendredi", "samedi"];
const MOIS_COURT = ["janv.", "févr.", "mars", "avr.", "mai", "juin", "juil.", "août", "sept.", "oct.", "nov.", "déc."];
const BURGUNDY = "#6E1A36";
const INK = "#1F1620";
const INK_2 = "#4D3F45";
const INK_3 = "#8A7780";
const LINE = "#ECE5E2";
const BAD_INK = "#B43A3A";

function labelJour(iso) {
  const [y, m, d] = iso.split("-").map(Number);
  const date = new Date(y, m - 1, d);
  return `${JOURS[date.getDay()]} ${date.getDate()} ${MOIS_COURT[date.getMonth()]}`;
}

const fieldStyle = { border: `1px solid ${LINE}`, borderRadius: 8, color: INK, background: "#fff" };

// Page publique en libre-service (aucune authentification, voir server.js
// allowlist /reservation/public/:token) — un client existant reçoit ce
// lien pour choisir un créneau chez son référent (voir plan Planning —
// onglet "Page de réservation"). Route montée dans App.js AVANT la porte
// de connexion staff (un client externe n'a pas de compte MyALFA).
function ReservationPublique() {
  const { token } = useParams();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null); // { date, heure }
  const [canal, setCanal] = useState("");
  const [motif, setMotif] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(null);

  useEffect(() => {
    httpClient
      .get(`/api/reservation/public/${token}`)
      .then(({ data }) => {
        setConfig(data.data);
        setCanal(data.data.canaux?.[0] || "");
      })
      .catch((err) => setError(err.response?.data?.message || "Ce lien n'est plus valide."))
      .finally(() => setLoading(false));
  }, [token]);

  const byDay = useMemo(() => {
    const groups = {};
    (config?.slots || []).forEach((s) => {
      groups[s.date] = groups[s.date] || [];
      groups[s.date].push(s.heure);
    });
    return groups;
  }, [config]);

  const submit = async () => {
    if (!selected) return;
    setSubmitting(true);
    setError(null);
    try {
      await httpClient.post(`/api/reservation/public/${token}`, { date: selected.date, heure: selected.heure, canal, motif });
      setConfirmed(selected);
    } catch (err) {
      setError(err.response?.data?.message || "Erreur lors de la confirmation.");
    } finally {
      setSubmitting(false);
    }
  };

  const Shell = ({ children }) => (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: "#FAF7F4" }}>
      <div style={{ maxWidth: 560, width: "100%", background: "#fff", borderRadius: 18, boxShadow: "0 24px 60px rgba(20,14,24,.15)", padding: "32px 28px" }}>
        {children}
      </div>
    </div>
  );

  if (loading) {
    return (
      <Shell>
        <div className="flex items-center justify-center py-10">
          <Loader2 className="animate-spin" size={24} style={{ color: BURGUNDY }} />
        </div>
      </Shell>
    );
  }

  if (!config) {
    return (
      <Shell>
        <p className="text-sm" style={{ color: BAD_INK }}>
          {error || "Ce lien n'est plus valide."}
        </p>
      </Shell>
    );
  }

  if (confirmed) {
    return (
      <Shell>
        <div className="flex items-center gap-2 mb-3" style={{ color: BURGUNDY }}>
          <Check size={20} />
          <h1 className="text-lg font-semibold m-0">Rendez-vous confirmé</h1>
        </div>
        <p className="text-sm" style={{ color: INK_2 }}>
          Votre rendez-vous est confirmé le <b className="capitalize">{labelJour(confirmed.date)}</b> à <b>{confirmed.heure}</b> ({config.dureeMinutes} min).
        </p>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="flex items-center gap-2 mb-1" style={{ color: BURGUNDY }}>
        <CalendarDays size={19} />
        <h1 className="text-lg font-semibold m-0" style={{ color: INK }}>
          {config.titre}
        </h1>
      </div>
      {config.messageAccueil && (
        <p className="text-sm mb-4" style={{ color: INK_3 }}>
          {config.messageAccueil}
        </p>
      )}

      {error && (
        <p className="text-xs mb-3" style={{ color: BAD_INK }}>
          {error}
        </p>
      )}

      {!selected ? (
        <div className="flex flex-col gap-3">
          {Object.keys(byDay).length === 0 && (
            <p className="text-sm" style={{ color: INK_3 }}>
              Aucun créneau disponible pour le moment.
            </p>
          )}
          {Object.entries(byDay).map(([date, heures]) => (
            <div key={date}>
              <div className="text-xs font-semibold capitalize mb-1.5" style={{ color: INK_2 }}>
                {labelJour(date)}
              </div>
              <div className="flex flex-wrap gap-2">
                {heures.map((h) => (
                  <button
                    key={h}
                    onClick={() => setSelected({ date, heure: h })}
                    className="text-xs font-semibold px-3 py-1.5"
                    style={{ border: `1px solid ${LINE}`, borderRadius: 8, color: INK_2, background: "#fff" }}
                  >
                    {h}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          <button onClick={() => setSelected(null)} className="flex items-center gap-1.5 text-xs self-start" style={{ color: INK_3 }}>
            <ArrowLeft size={13} /> Changer de créneau
          </button>
          <p className="text-sm" style={{ color: INK_2 }}>
            Créneau choisi : <b className="capitalize">{labelJour(selected.date)}</b> à <b>{selected.heure}</b> ({config.dureeMinutes} min)
          </p>
          {config.canaux.length > 1 && (
            <label className="flex flex-col gap-1">
              <span className="text-[11px] font-medium" style={{ color: INK_2 }}>
                Canal
              </span>
              <select value={canal} onChange={(e) => setCanal(e.target.value)} className="h-9 px-3 text-xs outline-none appearance-none cursor-pointer" style={fieldStyle}>
                {config.canaux.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </label>
          )}
          <label className="flex flex-col gap-1">
            <span className="text-[11px] font-medium" style={{ color: INK_2 }}>
              Motif du rendez-vous {config.motifObligatoire && <span style={{ color: BAD_INK }}>*</span>}
            </span>
            <textarea value={motif} onChange={(e) => setMotif(e.target.value)} rows={3} className="px-3 py-2 text-xs outline-none resize-y" style={fieldStyle} />
          </label>
          <button
            onClick={submit}
            disabled={submitting || (config.motifObligatoire && !motif.trim())}
            className="flex items-center justify-center gap-1.5 px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-50"
            style={{ background: BURGUNDY, borderRadius: 10 }}
          >
            <Check size={15} /> {submitting ? "Confirmation…" : "Confirmer le rendez-vous"}
          </button>
        </div>
      )}
    </Shell>
  );
}

export default ReservationPublique;
